


import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  Autocomplete,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { API_BASE_URL } from '../../../config';
import api from '../../../api/axiosInterceptor';

// Format JS Date to DD-MM-YYYY for display
const dateToDisplayString = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB').replace(/\//g, '-');
};

// Convert DD-MM-YYYY or DD/MM/YYYY to YYYY-MM-DD for date input value
const convertDisplayDateToISO = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.includes('/') ? dateStr.split('/') : dateStr.split('-');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export const AdminAssetValuationComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(true);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [formData, setFormData] = useState({
    asset_property_base: null,
    valuation_agency: '',
    valuation_work_ord_date: '',
    valuation_report_date: '',
    valuation_status: '',
    area: '',
    guideline_value: '',
    building_value: '',
    total_value: '',
    valuation_report: '',
  });

  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl. No', width: 70 },

    { id: 'name_of_fe', field: 'name_of_fe', headerName: 'Financial Establishment', width: 250 },
    { id: 'name_of_ca', field: 'name_of_ca', headerName: 'Competent Authority', width: 200 },

    { id: 'notification', field: 'notification', headerName: 'Notification', width: 150 },
    { id: 'date', field: 'date', headerName: 'Date', width: 120 },
    { id: 'notification_no', field: 'notification_no', headerName: 'Notification No', width: 150 },

    { id: 'property_detals', field: 'property_detals', headerName: 'Property Details', width: 200 },
    { id: 'survey_no', field: 'survey_no', headerName: 'Survey No', width: 120 },
    { id: 'property_type', field: 'property_type', headerName: 'Property Type', width: 150 },

    { id: 'valuation_agency', field: 'valuation_agency', headerName: 'Valuation Agency', width: 200 },
    { id: 'valuation_work_ord_date', field: 'valuation_work_ord_date', headerName: 'Work Order Date', width: 140 },
    { id: 'valuation_report_date', field: 'valuation_report_date', headerName: 'Report Date', width: 140 },
    { id: 'valuation_status', field: 'valuation_status', headerName: 'Status', width: 120 },
    { id: 'area', field: 'area', headerName: 'Area', width: 120 },
    { id: 'guideline_value', field: 'guideline_value', headerName: 'Guideline Value', width: 150 },
    { id: 'building_value', field: 'building_value', headerName: 'Building Value', width: 150 },
    { id: 'total_value', field: 'total_value', headerName: 'Total Value', width: 150 },
    { id: 'valuation_report', field: 'valuation_report', headerName: 'Valuation Report', width: 250 },

    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}><Edit /></IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)}><Delete /></IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchPropertyOptions();
    fetchData();
  }, []);

  const fetchPropertyOptions = () => {
    setPropertyLoading(true);
    axios.get(`${API_BASE_URL}/asset-mgmt/asset-property/`)
      .then(res => setPropertyOptions(res.data))
      .catch(() => {
        setSnackbarMessage('Failed to load Asset Property options.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => setPropertyLoading(false));
  };

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/asset-mgmt/asset-valuation/`)
      .then(response => {
        const apiData = response.data.map((item, index) => ({
          id: item.id,
          slNo: index + 1,
          name_of_fe: item.asset_property_base?.asset3_2_base?.fe?.name_of_fe || '-',
          name_of_ca: item.asset_property_base?.asset3_2_base?.fe?.name_of_ca || '-',
          notification: item.asset_property_base?.asset3_2_base?.notification || '-',
          date: item.asset_property_base?.asset3_2_base?.date ? dateToDisplayString(item.asset_property_base.asset3_2_base.date) : '-',
          notification_no: item.asset_property_base?.asset3_2_base?.notification_no || '-',

          property_detals: item.asset_property_base?.property_detals || '-',
          survey_no: item.asset_property_base?.survey_no || '-',
          property_type: item.asset_property_base?.property_type || '-',

          valuation_agency: item.valuation_agency || '-',
          valuation_work_ord_date: item.valuation_work_ord_date
            ? dateToDisplayString(item.valuation_work_ord_date)
            : '-',
          valuation_report_date: item.valuation_report_date
            ? dateToDisplayString(item.valuation_report_date)
            : '-',
          valuation_status: item.valuation_status || '-',
          area: item.area || '-',
          guideline_value: item.guideline_value || '-',
          building_value: item.building_value || '-',
          total_value: item.total_value || '-',
          valuation_report: item.valuation_report || '-',
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch(() => {
        setSnackbarMessage('Failed to load Asset Valuation data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedRecord(null);
    setFormData({
      asset_property_base: null,
      valuation_agency: '',
      valuation_work_ord_date: '',
      valuation_report_date: '',
      valuation_status: '',
      area: '',
      guideline_value: '',
      building_value: '',
      total_value: '',
      valuation_report: '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (record) => {
    const selectedProperty = propertyOptions.find(opt => opt.id === record.asset_property_base?.id) || null;
    setIsEditMode(true);
    setSelectedRecord(record);
    setFormData({
      asset_property_base: selectedProperty,
      valuation_agency: record.valuation_agency || '',
      valuation_work_ord_date: record.valuation_work_ord_date ? convertDisplayDateToISO(dateToDisplayString(record.valuation_work_ord_date)) : '',
      valuation_report_date: record.valuation_report_date ? convertDisplayDateToISO(dateToDisplayString(record.valuation_report_date)) : '',
      valuation_status: record.valuation_status || '',
      area: record.area || '',
      guideline_value: record.guideline_value || '',
      building_value: record.building_value || '',
      total_value: record.total_value || '',
      valuation_report: record.valuation_report || '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (record) => {
    setDeleteRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/asset-mgmt/asset-valuation/${deleteRecord.id}/`)
      .then(() => {
        setSnackbarMessage('Record deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchData();
      })
      .catch(() => {
        setSnackbarMessage('Failed to delete record.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteRecord(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.asset_property_base) errors.asset_property_base = 'Property Details selection is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = () => {
    if (!validateForm()) return;
    setSubmitLoading(true);
    const payload = {
      asset_property_base: formData.asset_property_base.id,
      valuation_agency: formData.valuation_agency,
      valuation_work_ord_date: formData.valuation_work_ord_date || null,
      valuation_report_date: formData.valuation_report_date || null,
      valuation_status: formData.valuation_status,
      area: formData.area,
      guideline_value: formData.guideline_value,
      building_value: formData.building_value,
      total_value: formData.total_value,
      valuation_report: formData.valuation_report,
    };
    const request = isEditMode
      ? api.patch(`/asset-mgmt/asset-valuation/${selectedRecord.id}/`, payload)
      : api.post(`/asset-mgmt/asset-valuation/`, payload);
    request.then(() => {
      setSnackbarMessage(isEditMode ? 'Record updated successfully.' : 'Record added successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchData();
    }).catch(() => {
      setSnackbarMessage(isEditMode ? 'Failed to update record.' : 'Failed to add record.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }).finally(() => {
      setSubmitLoading(false);
      setOpenFormDialog(false);
    });
  };

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">Admin Asset Valuation</Typography>
        <Button variant="contained" onClick={handleAdd}>Add Record</Button>
      </Box>

      {loading ? <LoadingSpinner size={30} /> : (
        <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1976d2',
            color: '#fff',
            fontWeight: 'bold',
          },
        }} />
      )}

      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{isEditMode ? 'Edit Record' : 'Add Record'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Autocomplete
            options={propertyOptions}
            getOptionLabel={(option) => {
              const feName = option.asset3_2_base?.fe?.name_of_fe || '';
              const propDetals = option.property_detals || '';
              return `${feName} - ${propDetals}`;
            }}
            loading={propertyLoading}
            value={formData.asset_property_base}
            onChange={(e, newValue) => setFormData({ ...formData, asset_property_base: newValue })}
            filterOptions={(options, { inputValue }) =>
              options.filter(option =>
                (option.asset3_2_base?.fe?.name_of_fe?.toLowerCase().includes(inputValue.toLowerCase()) ||
                 option.property_detals?.toLowerCase().includes(inputValue.toLowerCase()))
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Financial Establishment + Property Details"
                margin="dense"
                error={!!formErrors.asset_property_base}
                helperText={formErrors.asset_property_base}
                required
              />
            )}
          />
          <TextField label="Valuation Agency" value={formData.valuation_agency} fullWidth margin="dense"
            onChange={e => setFormData({ ...formData, valuation_agency: e.target.value })}
          />
          <TextField label="Work Order Date" type="date" value={formData.valuation_work_ord_date} fullWidth margin="dense"
            InputLabelProps={{ shrink: true }}
            onChange={e => setFormData({ ...formData, valuation_work_ord_date: e.target.value })}
          />
          <TextField label="Report Date" type="date" value={formData.valuation_report_date} fullWidth margin="dense"
            InputLabelProps={{ shrink: true }}
            onChange={e => setFormData({ ...formData, valuation_report_date: e.target.value })}
          />
          <TextField label="Valuation Status" value={formData.valuation_status} fullWidth margin="dense"
            onChange={e => setFormData({ ...formData, valuation_status: e.target.value })}
          />
          <TextField label="Area" value={formData.area} fullWidth margin="dense"
            onChange={e => setFormData({ ...formData, area: e.target.value })}
          />
          <TextField label="Guideline Value" value={formData.guideline_value} fullWidth margin="dense" multiline rows={2}
            onChange={e => setFormData({ ...formData, guideline_value: e.target.value })}
          />
          <TextField label="Building Value" value={formData.building_value} fullWidth margin="dense" multiline rows={2}
            onChange={e => setFormData({ ...formData, building_value: e.target.value })}
          />
          <TextField label="Total Value" value={formData.total_value} fullWidth margin="dense" multiline rows={2}
            onChange={e => setFormData({ ...formData, total_value: e.target.value })}
          />
          <TextField label="Valuation Report" value={formData.valuation_report} fullWidth margin="dense" multiline rows={4}
            onChange={e => setFormData({ ...formData, valuation_report: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)} disabled={submitLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSubmit} disabled={submitLoading}>
            {submitLoading ? <LoadingSpinner size={20} /> : (isEditMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={submitLoading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
